/**
 * Feed module related types
 */

import { PrivacySetting } from './user';

// Legacy imports for compatibility
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { FeedItem as LegacyFeedItem } from '../services/firebase/feed.service';

/**
 * Types of posts that can appear in the feed
 */
export type PostContentType = 'text' | 'training' | 'photo' | 'video' | 'competition' | 'promotion' | 'achievement';

/**
 * Media types that can be attached to posts
 */
export type MediaType = 'photo' | 'video';

/**
 * Tag types for posts
 */
export type TagType = 'user' | 'location' | 'technique';

/**
 * Main post interface - enhanced version
 */
export interface Post {
  id: string;
  authorId: string;
  authorUsername: string;
  authorDisplayName?: string;
  authorPhotoURL?: string;
  isInstructor?: boolean;
  isPeakSub?: boolean;
  content: string;
  contentType: PostContentType;
  relatedContentId?: string; // For posts about training, competition, etc.
  dateCreated: number;
  dateModified?: number;
  visibility: PrivacySetting;
  likes?: Record<string, boolean | any>; // Changed to allow any for legacy compatibility
  likesCount?: number;  // Original app uses plural 'likesCount'
  commentsCount?: number;  // Original app uses plural 'commentsCount'
  shareCount?: number;
  media?: PostMedia[];
  tags?: PostTag[];
  location?: PostLocation;
  pinnedComment?: string; // ID of a pinned comment
  isEdited?: boolean;
  isPinned?: boolean;
  isShared?: boolean;
  originalPost?: {
    id: string;
    authorId: string;
    authorUsername: string;
  };
  
  // Training specific fields
  techniques?: Record<string, {
    name: string;
    id?: string;
    category?: string;
    difficulty?: number;
  }>;
  trainingPartners?: Array<{
    id: string;
    name: string;
    avatarUrl?: string;
    beltRank?: string;
  }>;
  
  // Competition specific fields
  tournamentName?: string;
  duration?: number;
  gi?: boolean;
  trainedStyle?: string;
  winsCount?: number;
  lossesCount?: number;
  rank?: 0 | 1 | 2 | 3; // 0=none, 1=gold, 2=silver, 3=bronze

  // Belt promotion specific fields
  beltConfig?: {
    name: string; // e.g., "Blue Belt"
    baseColor: string; // e.g., "blue"
    rank?: string;
    level?: string; // 'adult' | 'junior'
    stripeCount?: number;
    stipeCount?: number; // Legacy/rules key: Firebase rules validate this misspelling; read-only fallback
    contrastColor?: string;
    contrastType?: string;
    stripeColor?: string;
    stripeBgColor?: string;
  };
  martialArtName?: string; // e.g., "Brazilian Jiu-Jitsu"
  instructor?: string; // Name of instructor who promoted the user
  
  // Legacy support
  key?: string;
  postType?: string;
  owner?: string;
  taggedFriends?: Record<string, any>;
  type?: 'training' | 'promotion' | 'competition' | 'post';
  
  // Additional legacy properties used in FeedItem component
  author?: {
    uid: string;
    displayName?: string;
    username?: string;
    photoURL?: string | null;
    isInstructor?: boolean;
    isPeakSub?: boolean;
  };
  note?: string;
  text?: string;
  title?: string;
  description?: string;
  message?: {
    content?: string;
    backgroundColor?: string;
    style?: string;
    textAlign?: string;
  } | string;
  hasLiked?: boolean;
  displayName?: string;
  instructorName?: string;
  photos?: Array<{
    url: string;
    aspectRatio?: number;
  }>;
  likedBy?: Record<string, any>;
  comments?: Record<string, any>;
  // Missing fields from feed pages
  itemRef?: string;
  username?: string;
  photoURL?: string | null;
  // Shadowban indicator (added by feed selectors for admin UI)
  isShadowbanned?: boolean;
}

/**
 * Media attached to a post
 */
export interface PostMedia {
  id: string;
  type: MediaType;
  url: string;
  thumbnailUrl?: string;
  aspectRatio?: number;
  caption?: string;
  width?: number;
  height?: number;
  duration?: number; // For videos - duration in seconds
  isProcessing?: boolean;
  order: number; // For ordering multiple media items
}

/**
 * Tag information for a post
 */
export interface PostTag {
  type: TagType;
  id: string;
  name: string;
  x?: number; // Optional position data for photo tags (0-1)
  y?: number;
}

/**
 * Location information for a post
 */
export interface PostLocation {
  id?: string;
  name: string;
  address?: string;
  // Original app uses flat structure, not nested coordinates
  latitude?: number;
  longitude?: number;
}

/**
 * Comment on a post
 */
export interface Comment {
  id: string;
  postId: string;
  authorId: string;
  authorUsername: string;
  authorDisplayName?: string;
  authorPhotoURL?: string;
  content: string;
  dateCreated: number;
  dateModified?: number;
  // Comment likes removed - not in original app
  // likes?: Record<string, boolean>;
  // likeCount: number;
  replyTo?: string; // Comment ID this is replying to
  mentions?: Array<{
    uid: string;
    username: string;
    isRead: boolean;
  }>;
  isEdited?: boolean;
  isPinned?: boolean;
}

/**
 * Feed item - represents a post in a user's feed
 */
export interface FeedItem {
  id: string;
  postId: string;
  userId: string; // The user whose feed this item is in
  dateCreated: number;
  source: 'following' | 'trending' | 'recommended' | 'promoted' | 'friend';
  order?: number; // For custom ordering in the feed
  isRead?: boolean;
  isHidden?: boolean;
  reason?: string; // Why this post is in the feed
}

/**
 * Post creation input
 */
export interface PostInput {
  content: string;
  contentType: PostContentType;
  relatedContentId?: string;
  visibility: PrivacySetting;
  mediaIds?: string[]; // IDs of previously uploaded media
  tags?: Omit<PostTag, 'name'>[]; // Tags without names (will be looked up)
  location?: PostLocation; // Location information
}

/**
 * Media upload result
 */
export interface MediaUploadResult {
  id: string;
  url: string;
  thumbnailUrl?: string;
  type: MediaType;
  aspectRatio?: number;
  width?: number;
  height?: number;
  duration?: number;
}

/**
 * Comment input
 */
export interface CommentInput {
  postId: string;
  content: string;
  replyTo?: string;
  mentionedUserIds?: string[];
}

/**
 * Legacy post interface for backward compatibility
 */
export interface LegacyPost {
  id: string;
  key: string;
  postType?: string;
  owner: string;
  hasLiked?: boolean;
  likeCount?: number;
  likesCount?: number;
  commentCount?: number;
  commentsCount?: number;
  username?: string;
  photoURL?: string | null;
  isInstructor?: boolean;
  authorDisplayName?: string;
  displayName?: string;
  instructorName?: string;
  author?: {
    uid: string;
    displayName?: string;
    username?: string;
    photoURL?: string | null;
    isInstructor?: boolean;
  };
  title?: string;
  note?: string;
  content?: string;
  text?: string;
  description?: string;
  postContent?: string;
  tournamentName?: string;
  duration?: number;
  techniques?: Record<string, any>;
  message?: {
    content?: string;
    backgroundColor?: string;
    style?: string;
    textAlign?: string;
  };
  dateCreated: number;
  itemRef?: string;
  likes?: Record<string, any>;
  likedBy?: Record<string, any>;
  comments?: Record<string, any>;
  location?: {
    name?: string;
    latitude?: number;
    longitude?: number;
  };
  photos?: Array<{
    url: string;
    aspectRatio?: number;
  }>;
  media?: Array<{
    type: 'image' | 'video';
    url: string;
    aspectRatio?: number;
  }>;
  taggedFriends?: Record<string, any>;
  type?: 'training' | 'promotion' | 'competition' | 'post';
}

/**
 * Post types that can be filtered in feed views
 */
export type FeedPostType =
  | 'training'
  | 'competition'
  | 'promotion'
  | 'beltPromotion'
  | 'exercise'
  | 'workoutSession'
  | 'story'
  | 'topic';

/**
 * Feed filter state for Redux
 */
export interface FeedFilterState {
  enabledTypes: FeedPostType[];
  isLoaded: boolean;
}

/**
 * Default feed filter - training-focused view
 * Shows trainings, competitions, promotions, and topics (for community feed) by default
 */
export const DEFAULT_FEED_FILTER: FeedPostType[] = [
  'training',
  'competition',
  'promotion',
  'beltPromotion',
  'topic'  // Topics are essential for community feed
];